/**
 * The modular figures in the game: a classic `Look` (with the wearer's equipment already laid over
 * it) is translated into the rig's layers — robe and trousers, hair and headwear, face, gear, the
 * weapon — and drawn into the same `SpriteSet` the renderer has always used, so walking, fighting,
 * camera headings, fabric lag and name tags all work unchanged. Frames are drawn the first time
 * they are shown, not all up front, so a crowded map does not stall while it fills.
 */
import { holdFor } from '../motions/hold';
import {
  ART_CX,
  FRAMES,
  buildBeasts,
  LOOKS,
  BODY_EXTRA,
  MOTIONS,
  STANCE_COUNT,
  STANCE_PH,
  EXTRA_POSES,
  type Look,
  PAD_TOP,
  type Pose,
  type SpriteSet,
  type View8,
  VIEWS,
  WALK_FRAMES,
  DIR8,
  LEFT_VIEWS,
} from '../sprites';
import { leftFinish } from './dir8';
import { density1x } from '../density';
import { canvas, cropFrame, markAnimationFrame, toArtRes } from '../pixel';
import { bottomLayer, robeLayer, topLayer } from './clothes';
import { DEFAULT_FACE, type FaceLook, REF_PART } from './face';
import type { FineSlot } from './face2';
import { compose, FRAME, faceLayer, RIG_RES, hairLayer, headwearLayer, type Layer, MODEL0 } from './figure';
import {
  beltLayer,
  capeLayer,
  itemsLayer,
  type Loadout,
  pauldronLayer,
  shoesLayer,
  stoleLayer,
} from './gear';
import { type BodyShape, GROUND, skeleton } from './skeleton';
import { newBodyLayer, newSkeleton } from './newBody';
import { gameFaceLayer } from './faces2/live';
import { outfit2x } from './outfit2x/switch';
import { isStarter, starterLayers } from './outfit2x/starter';
import {
  nativeFashionLayers,
  nativeUniformLayers,
  nBelt,
  nBottom,
  nCape,
  nHair,
  nHeadwear,
  nPauldrons,
  nRobe,
  nShoes,
  nStole,
  nTop,
} from './outfit2x/convert';
import { nativeHairLayer } from './outfit2x/hair2x';
import { decodeGaze } from './gaze';

/** The new faces (rig/faces2, lane C0-FACE); false: the old face (figure.ts `faceLayer`). */
const FACES2 = true;

/** How much higher the grown body carries the head than the classic figure's, in art pixels. */
const GROWN_RISE = 9;
import { toolLayer } from './tools';
import { bakedFrame } from './bake';
import { cachedFrame } from './frameCache';
import { litFinish } from './finish';
import { drawElsewhere, hurry, type Job, type Lane, whenWorkersFail, workersOn } from './workers';
import { UNIFORMS, uniformLayers, uniformTier } from './uniforms';
import { FASHION_BY_ID, fashionLayers } from './fashion';
import { clothAt, clothDefaultOn, swayed, withCloth } from './cloth';
import { figureB3 } from './b3/flag';
import { frameOfB3 } from './b3/figureB3';
import { swordLayer } from './swordParts';
import { BOTTOMS } from './clothes';
import {
  bladeLines,
  carryOf,
  type BladeLine,
  stowStep,
  WEAPON_BY_ID,
  type WeaponDesign,
  weaponLayer,
} from './weapons';

/** Everything the rig needs to draw one figure. */
export interface Appearance {
  body: BodyShape;
  layers: Layer[];
  armed: boolean;
  /** The look it was built from, and a key naming it, so a worker can build the same figure. */
  look?: Look;
  key?: string;
  /** The weapon in hand, if any (its blade is recorded for every frame; see `bladesIn`). */
  weapon?: WeaponDesign;
}

/** Where the weapon lies in each rig frame drawn, for effects that follow the blade. */
const BLADES = new WeakMap<HTMLCanvasElement, BladeLine[]>();

/** The weapon(s) held in a rig frame, in the frame's art pixels (empty when unarmed). */
export const bladesIn = (img: HTMLCanvasElement): readonly BladeLine[] => BLADES.get(img) ?? [];

/** The drawn hairstyle (rig/hair.ts) for each kind of hair a look names: [for a man, for a woman]. */
const HAIR: Record<Look['hairStyle'], readonly [string, string]> = {
  ponytail: ['hero', 'hero'],
  curtain: ['halfdown', 'longbangs'],
  bun: ['scholarbun', 'highbun'],
  loose: ['halfdown', 'longbangs'],
  buns: ['twinlowbuns', 'twinloops'],
  messy: ['crop', 'crop'],
  topknot: ['topknot', 'highbun'],
};

const HEADWEAR: Partial<Record<Look['headwear'], string>> = {
  guan: 'guan',
  tiara: 'guan',
  gem: 'pin',
  pin: 'pin',
  band: 'band',
};

/** The classic weapon, in the look's own colours. */
function weaponOf(look: Look): WeaponDesign | undefined {
  if (look.weapon === 'none') return undefined;
  const base =
    WEAPON_BY_ID[look.weapon === 'sabre' ? 'dao' : look.weapon === 'staff' ? 'staff' : 'longSword']!;
  return {
    ...base,
    // The classic blades are measured from the hand; keep their length.
    ahead: look.weapon === 'staff' ? base.ahead : Math.max(10, look.wlen),
    // A staff keeps its own wood (the look's colours are a blade's: steel, guard, hilt).
    colours:
      look.weapon === 'staff'
        ? base.colours
        : {
            blade: look.blade,
            edge: look.bladeS,
            guard: look.guard,
            grip: look.hilt,
            tassel: look.tassel,
          },
  };
}

/** Faces for people in the game, picked per sex from the fine face parts (face2.ts), the reference first. */
const FACES: Record<'male' | 'female', Record<FineSlot, readonly string[]>> = {
  male: {
    eyes: [REF_PART, 'phoenix', 'narrow', 'fierce', 'gentle'],
    brows: [REF_PART, 'sword', 'thick', 'sword'],
    nose: [REF_PART, 'high', 'broad'],
    mouth: [REF_PART, 'stern', 'wide'],
  },
  female: {
    eyes: [REF_PART, 'phoenix', 'round', 'gentle'],
    brows: [REF_PART, 'arch', 'thin', 'worried'],
    nose: [REF_PART, 'small', 'high'],
    mouth: [REF_PART, 'full', 'smile'],
  },
};

/**
 * A person's face parts, chosen from what does not change with what they wear (skin, hair, eyes,
 * figure), so everyone keeps one face of their own and different people look different.
 */
function faceOf(look: Look): Record<FineSlot, string> {
  const who = `${look.skin}|${look.hair}|${look.hairStyle}|${look.eye}|${look.iris ?? ''}|${look.female ? 1 : 0}`;
  const pick = (slot: FineSlot) => {
    let h = 2166136261;
    for (const ch of `${slot}:${who}`) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
    const list = FACES[look.female ? 'female' : 'male'][slot];
    return list[(h >>> 0) % list.length]!;
  };
  return { eyes: pick('eyes'), brows: pick('brows'), nose: pick('nose'), mouth: pick('mouth') };
}

/** A classic look in the rig's terms: body, clothes, hair, face, gear and weapon. */
export function appearanceOf(given: Look): Appearance {
  // Cloth that flows (cloth.ts): the look's own say, else the page's setting, written into the look so a worker
  // draws the same and the frames of either are kept apart.
  const look: Look = { ...given, cloth: given.cloth ?? clothDefaultOn() };
  const skin = { ...MODEL0, skin: look.skin, skinS: look.skinS, eye: look.eye };
  const weapon = weaponOf(look);
  const layers: Layer[] = [];
  /** A woman in the wide-cut robe (no uniform, no short robe): her own cut, the ruqun (see the robe below). */
  const womanCut = !!look.female && !!look.hanfu && !look.school && !look.shortRobe && !look.sleeveless;
  /** The player's starter pair drawn natively at the figure grid (lane C0-OUTFIT2X, outfit2x/starter.ts). */
  const starter = outfit2x() && isStarter(look);
  // Every other look's clothes, hair and headwear drawn natively too (outfit2x/convert.ts), the same parts and slots.
  const nat = outfit2x();
  const F = nat
    ? { bottom: nBottom, shoes: nShoes, top: nTop, robe: nRobe, belt: nBelt, cape: nCape, pauldron: nPauldrons, stole: nStole, hair: nHair, headwear: nHeadwear }
    : { bottom: bottomLayer, shoes: shoesLayer, top: topLayer, robe: robeLayer, belt: beltLayer, cape: capeLayer, pauldron: pauldronLayer, stole: stoleLayer, hair: hairLayer, headwear: headwearLayer };
  // The body (rig/newBody.ts, lane C0-CHAR 2026-10-10): Model 0's proportions on C0's head, grown to adult size.
  layers.push(
    newBodyLayer(
      skin,
      // Put away, the hand only grips while it draws the weapon or puts it away.
      !weapon
        ? false
        : look.sheathed
          ? (sk) => (sk.pose === 'sheathe' || sk.pose === 'walkSheathe') && stowStep(sk, true).mode !== 'stow'
          : true,
    ),
  );

  // A fashion outfit (fashion.ts) is worn instead of the clothes and the school's uniform.
  const fashion = look.fashion ? FASHION_BY_ID[look.fashion]?.[look.female ? 'female' : 'male'] : undefined;
  const uniform = fashion
    ? fashion
    : look.school
      ? uniformTier(
          UNIFORMS[look.school]?.[look.female ? 'female' : 'male'],
          look.schoolTier ?? 0,
          !!look.female,
        )
      : undefined;
  if (starter) layers.push(...starterLayers(look));
  else if (fashion) layers.push(...(nat ? nativeFashionLayers(fashion) : fashionLayers(fashion)));
  else if (uniform) layers.push(...(nat ? nativeUniformLayers(uniform) : uniformLayers(uniform)));
  else {
    // Trousers under everything; a short robe shows them wrapped at the shin.
    layers.push(
      F.bottom(
        look.shortRobe
          ? { id: 'wrapped', colour: look.inner, wrap: look.outerH }
          : { id: 'trousers', colour: look.innerS, wrap: look.outerH },
      ),
    );
    layers.push(F.shoes({ id: look.shortRobe ? 'boots' : 'cloth', colour: look.boots }));
    if (look.shortRobe || look.sleeveless) {
      layers.push(
        F.top({
          id: look.sleeveless ? 'vest' : 'tunic',
          colour: look.outer,
          trim: look.trim,
          inner: look.inner,
        }),
      );
      layers.push(F.belt({ colour: look.sash, buckle: look.trim }));
    } else {
      // The long robe over an inner shirt, sashed at the waist.
      layers.push(F.top({ id: 'cross', colour: look.inner, trim: look.trim, inner: look.inner }));
      layers.push(
        F.robe({
          // A woman's wide-sleeved robe is the high-waisted ruqun (the player as a woman, looks/heroes.ts heroF);
          // an equipped coat colours it rather than being worn over it, so her figure keeps a woman's cut.
          id: look.hanfu ? (womanCut ? 'ruqun' : 'hanfu') : 'robe',
          colour: womanCut && look.coat ? look.coat.c : look.outer,
          trim: womanCut && look.coat ? look.coat.edge : look.trim,
          inner: look.inner,
          sash: look.sash,
        }),
      );
    }
  }
  // A school uniform or a fashion outfit hides the coat (costume C1, decided 10-11): the coat is not worn over them.
  if (look.coat && !womanCut && !starter && !uniform)
    layers.push(
      F.robe({
        id: 'coat',
        colour: look.coat.c,
        trim: look.coat.edge,
        inner: look.inner,
        sash: look.sash,
      }),
    );
  if (look.cape) layers.push(F.cape({ id: 'long', colour: look.cape, clasp: look.guard }));
  if (look.pauldron) layers.push(F.pauldron({ plate: look.pauldron.c, edge: look.pauldron.s }));
  const fur = look.stole ?? look.pelt;
  if (fur) layers.push(F.stole({ fur: fur.c }));

  const face: FaceLook = {
    ...DEFAULT_FACE,
    iris: look.iris ?? look.eye,
    ...faceOf(look),
    beard: look.beard === 'long' ? 'long' : 'none',
    mustache: look.beard === 'long' ? 'whiskers' : 'none',
    sideburns: look.beard === 'stubble' ? 'short' : 'none',
    marks: look.blush ? ['blush'] : [],
    facialHair: look.beardColor,
  };
  // The face (lane C0-FACE, 2026-10-10): picked by who the person is (Look.faceId), the expression from the pose
  // (rig/faces2/live.ts). FACES2 false brings back the old face built above.
  layers.push(FACES2 ? gameFaceLayer(look) : faceLayer(face, skin, look.hair));
  // The player as a man wears his hair up in a topknot, no ribbons, in every uniform (lane C0-OUTFIT2X: the sexes
  // apart); an outfit's own hairstyle still wins.
  const manPlayer = nat && !look.female && !!look.faceId?.startsWith('hero') && !fashion?.hairStyle;
  if (manPlayer && !starter) layers.push(nativeHairLayer('hightail', look.hair, { noRibbon: true }));
  else if (!starter) layers.push(F.hair(HAIR[fashion?.hairStyle ?? look.hairStyle][look.female ? 1 : 0], look.hair));
  // A uniform's own headwear (an official's cap) is worn instead of the figure's.
  if (!uniform?.headwear?.length && !starter) {
    const hw = HEADWEAR[look.headwear];
    if (hw) layers.push(F.headwear(hw));
    if (look.ribbon && !manPlayer) layers.push(F.headwear('ribbon'));
  }
  if (look.hat) layers.push(F.headwear('douli'));
  if (look.hat?.veil) layers.push(F.headwear('veil'));
  if (look.foxEars) layers.push(F.headwear('foxEars'));
  if (look.mask) layers.push(F.headwear('mask'));

  const loadout: Loadout = {};
  if (look.pendant && !starter) loadout.waistA = 'jade';
  layers.push(itemsLayer(loadout));
  // In hand, or put away where it is carried (a sword in its scabbard at the hip, a spear on the
  // back), and moved from one to the other by the 'sheathe' and 'unsheathe' poses.
  // A one-handed jian is painted from its part spec (swordParts/, lane SWORD); other weapons as before.
  if (weapon) layers.push(swordLayer(weapon, look) ?? weaponLayer(weapon, { sheathed: !!look.sheathed, scabbard: look.scabbard }));
  layers.push(toolLayer());

  const body: BodyShape = {
    build: look.build ?? (look.pelt || look.pauldron ? 'stocky' : 'average'),
    female: !!look.female,
    height: (look.tall ?? 0) > 0 ? 'tall' : 'average',
    ...(look.age ? { age: look.age } : {}),
    ...(look.bearing ? { bearing: look.bearing } : {}),
    // Each person stands in a stance of their own (`SpriteSet.stance`); children and elders keep
    // their own carriage, as does anyone given a bearing.
    ...(look.bearing || look.age === 'child' || look.age === 'elder' ? {} : { stanced: true }),
    wielding: !!weapon && !look.sheathed,
    ...(weapon && carryOf(weapon) === 'back' ? { stowBack: true } : {}),
    // How a drawn weapon is carried at ease: the look's own hold or its kind's default (motions/hold.ts).
    ...(weapon && !look.sheathed && holdFor(look.hold, weapon.kind)
      ? { hold: holdFor(look.hold, weapon.kind)! }
      : {}),
    // The longer, adult body, the head riding on the neck at its own size.
    grown: true,
  };
  const drawn = weapon && !look.sheathed ? weapon : undefined;
  // With cloth on, robe skirts and skirts trail (cloth.ts `swayed`); trousers stay on the legs.
  const skirt = BOTTOMS.find((b) => b.id === uniform?.bottom.id)?.fit === 'skirt';
  const worn = look.cloth
    ? layers.map((l) => (l.id === 'robe' || (l.id === 'bottom' && skirt) ? swayed(l) : l))
    : layers;
  return {
    body,
    layers: worn,
    armed: !!drawn,
    look,
    key: JSON.stringify(look),
    ...(drawn ? { weapon: drawn } : {}),
  };
}

/** Counts for dev tools: frames shown while the right one was still being drawn, and frames drawn. */
export const rigStats = { miss: 0, cloth: 0, drawn: 0 };

/**
 * Set while frames are asked for ahead of need (not to be shown now): they are queued in `lane`
 * (else the usual one) and a frame already queued is not hurried, as a frame on screen is.
 */
let warming: { lane?: Lane } | null = null;
/** Hurries a frame already asked for: to `lane`, else as one wanted on screen now. */
type Rush = (lane?: Lane) => void;
/** Runs `f`, which asks for frames ahead of need (see `warming`). */
export function ahead<T>(f: () => T, lane?: Lane): T {
  const was = warming;
  warming = { lane };
  try {
    return f();
  } finally {
    warming = was;
  }
}

/**
 * Builds `n` frames on first read, so an array of frames costs nothing until a frame is shown.
 */
function lazyFrames(
  n: number,
  make: (i: number) => HTMLCanvasElement,
  fallback: () => HTMLCanvasElement,
  /** Asks for frame `i`; what it returns hurries it along (by default: it is wanted on screen). */
  ask: (i: number, first: boolean, got: (c: HTMLCanvasElement) => void) => Rush | undefined,
  hasFrame: () => boolean,
): HTMLCanvasElement[] {
  const cache: HTMLCanvasElement[] = [];
  const queued = new Map<number, Rush | undefined>();
  const get = (i: number): HTMLCanvasElement => {
    const c = cache[i];
    if (c) return c;
    // Browser frame requests never compose a new figure inside the render loop.
    // Worker/idle jobs publish the finished frame; non-browser callers remain synchronous.
    if (typeof requestAnimationFrame === 'undefined' && !workersOn() && canDraw())
      return (cache[i] = make(i));
    // Being drawn elsewhere: the nearest frame of the same cycle drawn before it stands in (the
    // cycle plays on, coarser, instead of holding one frame), else a frame of the figure.
    let stand: HTMLCanvasElement | undefined;
    for (let k = 1; k < n && !stand; k++) stand = cache[(i - k + n) % n];
    if (!warming) rigStats.miss++;
    // Until the figure has a picture, request only one frame of this cycle. Otherwise every
    // animation phase queues as urgent before the first reply, delaying other new figures.
    if (!hasFrame() && queued.size) return fallback();
    if (!queued.has(i))
      queued.set(
        i,
        ask(i, !hasFrame(), (c) => (cache[i] ??= c)),
      );
    // Asked for again while still not drawn: it is on screen now, so it goes ahead of frames
    // asked for ahead of need (or wanted sooner than first asked).
    else if (!warming || warming.lane) queued.get(i)?.(warming?.lane);
    // A baked frame arrives at once.
    return cache[i] ?? stand ?? fallback();
  };
  return new Proxy(new Array<HTMLCanvasElement>(n), {
    get(target, key, recv) {
      if (typeof key === 'string' && /^\d+$/.test(key)) {
        const i = Number(key);
        return i >= n ? undefined : get(i);
      }
      if (key === Symbol.iterator)
        return function* () {
          for (let i = 0; i < n; i++) yield get(i);
        };
      return Reflect.get(target, key, recv) as unknown;
    },
  });
}

/**
 * Milliseconds of new figure frames drawn while a frame is being painted. A crowd coming into view
 * (a guild war) would otherwise stall the game drawing hundreds of frames at once: past the budget
 * a frame already drawn stands in, and the rest are drawn in idle time.
 */
const FRAME_BUDGET = 6;
let spent = 0;
let resetQueued = false;
/** How long drawing one frame takes, on average lately: work only starts where it fits. */
let perFrame = 6;
function canDraw(): boolean {
  // One frame at least per animation frame, however long frames take, so a crowd keeps filling in.
  return spent === 0 || spent + perFrame <= FRAME_BUDGET;
}
/** Counts a frame's drawing time against this animation frame's budget. */
function charge(ms: number): void {
  perFrame += (ms - perFrame) * 0.1;
  spent += ms;
  if (resetQueued || typeof requestAnimationFrame === 'undefined') return;
  resetQueued = true;
  requestAnimationFrame(() => {
    spent = 0;
    resetQueued = false;
  });
}

/** Frames waiting to be drawn, in the browser's idle time between animation frames. */
const pending: (() => void)[] = [];
/** Frames for figures with none drawn yet (shown as nothing): drawn before any other waiting frame. */
const urgent: (() => void)[] = [];
let pumping = false;
const next = () => (urgent.shift() ?? pending.shift())!();
/** Frames queued for idle time so far, and how many still wait (for the loading screen). */
export const idleFrames = { queued: 0, waiting: (): number => urgent.length + pending.length };
type Idle = (
  cb: (d: { timeRemaining(): number; didTimeout?: boolean }) => void,
  o?: { timeout: number },
) => number;
const idle: Idle =
  (globalThis as { requestIdleCallback?: Idle }).requestIdleCallback?.bind(globalThis) ??
  ((cb) => setTimeout(() => cb({ timeRemaining: () => 6 }), 16) as unknown as number);
function later(job: () => void, first = false): void {
  idleFrames.queued++;
  (first ? urgent : pending).push(job);
  if (pumping) return;
  pumping = true;
  const pump = (d: { timeRemaining(): number; didTimeout?: boolean }) => {
    // Only what fits in the idle time left, but one at least when the browser has had none to
    // give for a while, so a busy game still catches up.
    const left = () => urgent.length + pending.length;
    if (d.didTimeout) next();
    while (left() && d.timeRemaining() > perFrame + 1) next();
    if (left()) idle(pump, { timeout: 100 });
    else pumping = false;
  };
  idle(pump, { timeout: 100 });
}

// If the workers cannot run here after all, what they were asked for is drawn on the main thread.
whenWorkersFail((req, done, first) =>
  later(() => {
    const { c, blades } = frameOf(appearanceOf(req.look), req.view, req.pose, req.ph, req.lag, req.blink);
    done(c, blades);
  }, first),
);

/** Shown for a figure none of whose frames are drawn yet: nothing, until its first frame is ready. */
let blank: HTMLCanvasElement | undefined;
const blankFrame = () => (blank ??= canvas(FRAME.w, FRAME.h));

/** One frame of a figure, cut to what is drawn, and where its weapon lies (on either thread). */
export function frameOf(
  app: Appearance,
  view: View8,
  pose: Pose,
  ph = 0,
  lag = 0,
  blink = false,
): { c: HTMLCanvasElement; blades: BladeLine[] } {
  // The B3 figure (lane C0-PROPORTION, rig/b3/figureB3.ts), behind its temporary build flag (off).
  if (figureB3()) return frameOfB3(app, view, pose, ph, lag, blink);
  // The pose on the new body's proportions and stance (rig/newBody.ts), with its cloth laid on (cloth.ts).
  const bare = newSkeleton(skeleton(view, pose, ph, lag, app.body, blink));
  const cloth = app.look?.cloth ? clothAt(pose, ph, app.body) : undefined;
  const sk = cloth ? withCloth(bare, cloth, lag) : bare;
  // Kept at the rig's double resolution, as the studio shows it: the world is painted at two
  // canvas pixels per art pixel (renderer.ts `res`), so figures keep their detail on screen.
  // The lit finish (rig/finish.ts): form light from the upper right and a dark coloured outline.
  // A left-facing view (rig/dir8.ts) is lit as it will be seen, mirrored.
  const raw = compose(app.layers, sk, { res: RIG_RES });
  const c = cropFrame(sk.left ? leftFinish(raw) : litFinish(raw));
  // The 1x mode (density.ts): the figure brought down to whole art pixels, as the 1× bake is.
  return { c: density1x() ? toArtRes(c) : c, blades: app.weapon ? bladeLines(app.weapon, sk) : [] };
}

/** The views a figure's front three-quarter set holds besides itself: the other four, and the left-facing three. */
const OTHER_VIEWS: readonly View8[] = [...VIEWS.filter((v) => v !== 'front3'), ...(DIR8 ? LEFT_VIEWS : [])];

/** One view of an appearance as a sprite set; the front three-quarter set also holds the other views. */
export function rigSet(
  app: Appearance,
  view: View8 = 'front3',
  shared: {
    any?: HTMLCanvasElement;
    /** The player's own figure: its frames are drawn before everyone else's (see workers.ts). */
    mine?: boolean;
    /** Asks for the player's everyday frames in every view, once the figure has a picture. */
    core?: () => void;
    /** A frame has been asked for ahead of the queue while the figure has none. */
    first?: boolean;
    /** How soon the player's everyday frames have been asked for (see `focus`). */
    warmed?: Lane;
  } = {},
): SpriteSet {
  // Remember which pose each frame is, so it can be redrawn with fabric still in motion.
  const meta = new Map<HTMLCanvasElement, [Pose, number]>();
  const listeners = new Set<(image: HTMLCanvasElement) => void>();
  let prepareRequested = false;
  let prepareQueued = false;
  const prepareCycles = () => {
    if (!prepareRequested || prepareQueued || !shared.any) return;
    prepareQueued = true;
    for (const frames of [set.idle, set.walk]) {
      if (!frames) continue;
      for (let i = 0; i < frames.length; i++)
        later(() => {
          ahead(() => void frames[i]);
        });
    }
  };
  /** Notes what a finished frame is, wherever it was drawn. */
  const keep = (
    c: HTMLCanvasElement,
    blades: BladeLine[],
    pose: Pose,
    ph: number,
    lag: number,
    blink: boolean,
  ) => {
    markAnimationFrame(c);
    rigStats.drawn++;
    shared.any ??= c;
    shared.core?.();
    if (blades.length) BLADES.set(c, blades);
    if (!lag && !blink) meta.set(c, [pose, ph]);
    for (const ready of listeners) ready(c);
    prepareCycles();
    return c;
  };
  const draw = (pose: Pose, ph = 0, lag = 0, blink = false) => {
    const t0 = performance.now();
    const { c, blades } = frameOf(app, view, pose, ph, lag, blink);
    charge(performance.now() - t0);
    return keep(c, blades, pose, ph, lag, blink);
  };
  /**
   * How soon a frame is wanted (see `LANES` in workers.ts): `first` when the figure has nothing to
   * show yet; the player's everyday cycles (`core`) ahead of the rest of their frames, and the
   * player's ahead of everyone else's.
   */
  const laneFor = (first: boolean, core: boolean): Lane =>
    first
      ? shared.mine
        ? 'mine'
        : 'urgent'
      : (warming?.lane ?? (shared.mine ? (core ? 'mineCore' : 'mineWarm') : 'later'));
  /** Hurries a frame asked for earlier that is now wanted on screen. */
  const rush = (job: Job | null): Rush | undefined =>
    job ? (lane) => hurry(job, lane ?? (shared.mine ? 'mine' : 'now')) : undefined;
  /**
   * A frame drawn off the main thread when workers can (see workers.ts), else in idle time; `got`
   * receives it. Returns the worker's job (to hurry it), if any.
   */
  const fetch = (
    pose: Pose,
    ph: number,
    lag: number,
    blink: boolean,
    lane: Lane,
    got: (c: HTMLCanvasElement) => void,
  ): Job | null => {
    // Baked ahead of time (npm run bake): cut from its atlas, nothing to draw.
    // Or drawn on an earlier load and kept in this browser (frameCache.ts).
    const baked = app.key
      ? (bakedFrame(app.key, view, pose, ph, lag, blink) ?? cachedFrame(app.key, view, pose, ph, lag, blink))
      : undefined;
    if (baked) {
      got(keep(baked.c, baked.blades, pose, ph, lag, blink));
      return null;
    }
    const job =
      app.look && app.key
        ? drawElsewhere(
            { look: app.look, key: app.key, view, pose, ph, lag, blink },
            (c, blades) => got(keep(c, blades, pose, ph, lag, blink)),
            lane,
          )
        : null;
    if (!job) later(() => got(draw(pose, ph, lag, blink)), lane === 'mine' || lane === 'urgent');
    return job;
  };
  const extras = new Map<string, HTMLCanvasElement>();
  const fallback = () => shared.any ?? blankFrame();
  /** Frames asked for and not drawn yet, each with how to hurry it along. */
  const waiting = new Map<string, Rush | undefined>();
  /** A frame drawn now if this frame's budget allows, else a stand-in while it waits its turn. */
  const lazy = (
    key: string,
    store: Map<string, HTMLCanvasElement>,
    make: () => HTMLCanvasElement,
    ask: (first: boolean, got: (c: HTMLCanvasElement) => void) => Rush | undefined,
    /** Shown until it is drawn: by default whatever frame of the figure is ready. */
    stand: () => HTMLCanvasElement = fallback,
  ) => {
    const c = store.get(key);
    if (c) return c;
    if (typeof requestAnimationFrame === 'undefined' && !workersOn() && canDraw()) {
      const made = make();
      store.set(key, made);
      return made;
    }
    if (!warming) rigStats.miss++;
    if (!waiting.has(key)) {
      // Only one frame jumps the queue while the figure has none: a fighter's first sight asks for
      // hundreds of motion frames ahead of need, and all of them urgent held up other new figures.
      const first = !shared.any && !shared.first;
      if (first) shared.first = true;
      waiting.set(
        key,
        ask(first, (c) => {
          if (!store.has(key)) store.set(key, c);
        }),
      );
    }
    // Asked for again while still not drawn: it is on screen now (or wanted sooner).
    else if (!warming || warming.lane) waiting.get(key)?.(warming?.lane);
    // A baked frame arrives at once.
    return store.get(key) ?? stand();
  };
  const extra = (pose: Pose, ph = 0, blink = false, stand?: () => HTMLCanvasElement) =>
    lazy(
      `${pose}:${ph}:${blink ? 1 : 0}`,
      extras,
      () => draw(pose, ph, 0, blink),
      (first, got) => rush(fetch(pose, ph, 0, blink, laneFor(first, false), got)),
      // Not drawn yet: what the caller offers (a motion's nearest key pose), or the figure just
      // stands (not whatever pose it drew first — an attack or a flinch flashing up while it
      // stands still).
      stand ?? (() => set.idle[pose === 'idle' ? ph % FRAMES : 0]!),
    );
  const moving = new Map<string, HTMLCanvasElement>();
  const cloth = (img: HTMLCanvasElement, sway: number, _flutter: number) => {
    const m = meta.get(img);
    if (!m || !sway) return img;
    // The fabric is redrawn at the same step of the pose: shifting the phase for a flutter moved the
    // whole figure (legs, arms, body) to another point of the stride, so a walk shook. Hair and
    // ribbons still wave with the stride itself (see `flutter` in figure.ts).
    const key = `${m[0]}:${m[1]}:${sway}`;
    const c = moving.get(key);
    if (c) return c;
    rigStats.cloth++;
    // Not drawn yet: show the still frame this once and draw the moving one between frames, so a
    // figure breaking into a run never stalls the game while its streaming cloth is drawn.
    if (!queued.has(key)) {
      queued.add(key);
      fetch(m[0], m[1], sway, false, laneFor(false, false), (c) => {
        moving.set(key, c);
        queued.delete(key);
      });
    }
    return img;
  };
  const queued = new Set<string>();
  const posed = new Map<string, HTMLCanvasElement>();
  const cycle = (pose: Pose, n: number, base = 0) =>
    lazyFrames(
      n,
      (i) => draw(pose, base + i),
      fallback,
      (i, first, got) => rush(fetch(pose, base + i, 0, false, laneFor(first, true), got)),
      () => !!shared.any,
    );
  const once = (pose: Pose) => () =>
    lazy(
      pose,
      posed,
      () => draw(pose),
      (first, got) => rush(fetch(pose, 0, 0, false, laneFor(first, true), got)),
    );
  const set: SpriteSet = {
    prepare: (ready) => {
      listeners.add(ready);
      prepareRequested = true;
      for (const image of meta.keys()) ready(image);
      prepareCycles();
      if (!shared.any) void set.idle[0];
    },
    cloth,
    extra,
    idle: cycle('idle', FRAMES),
    walk: cycle('walk', WALK_FRAMES),
    sprint: cycle('sprint', WALK_FRAMES),
    ready: cycle('ready', FRAMES),
    armed: app.armed,
    poses: {},
    ax: ART_CX,
    ay: GROUND + 1 + PAD_TOP,
    // Name tags sit over the head: the grown body carries it higher than the classic figure's.
    h: 56 + BODY_EXTRA + GROWN_RISE,
    drawScale: 1,
  } as SpriteSet;
  for (const p of ['atk', 'block', 'cast', 'sit', 'jump'] as const)
    Object.defineProperty(set, p, { get: once(p), enumerable: true });
  for (const p of EXTRA_POSES) Object.defineProperty(set.poses, p, { get: once(p), enumerable: true });
  if (view === 'front3') {
    const views: Partial<Record<View8, SpriteSet>> = {};
    for (const v of OTHER_VIEWS) {
      let s: SpriteSet | undefined;
      Object.defineProperty(views, v, { get: () => (s ??= rigSet(app, v, shared)), enumerable: true });
    }
    set.views = views;
    set.focus = (soon = true) => {
      shared.mine = true;
      // Once, or again when wanted sooner (the figure with the weapon put away becoming the one shown).
      if (shared.warmed === 'mineCore' || shared.warmed === (soon ? 'mineCore' : 'mineWarm')) return;
      shared.warmed = soon ? 'mineCore' : 'mineWarm';
      const lane = shared.warmed;
      shared.core = () => {
        shared.core = undefined;
        const all = [set, ...OTHER_VIEWS.map((v) => views[v]!)];
        // Standing, walking, the stance and running in every view first (turning or setting off
        // must never wait), then the single poses, then putting the weapon away and drawing it.
        ahead(() => {
          for (const k of ['idle', 'walk', 'ready', 'sprint'] as const)
            for (const s of all) {
              const frames = s[k];
              if (frames) for (let i = 0; i < frames.length; i++) void frames[i];
            }
          for (const s of all) {
            for (const p of ['atk', 'block', 'cast', 'jump', 'sit'] as const) void s[p];
            for (const p of EXTRA_POSES) void s.poses[p];
          }
        }, lane);
        ahead(() => {
          for (const s of all)
            for (const p of ['sheathe', 'unsheathe'] as const)
              for (let f = 0; f < FRAMES; f++) s.extra?.(p, f);
        }, 'mineWarm');
      };
      if (shared.any) shared.core();
      else void set.idle[0];
    };
  }
  // The figure in each of its stances: this set, but for its standing frames.
  if (app.body.stanced && !app.body.stance) {
    const stances = new Map<number, SpriteSet>([[0, set]]);
    set.stance = (k) => {
      const i = ((k % STANCE_COUNT) + STANCE_COUNT) % STANCE_COUNT;
      let s = stances.get(i);
      if (!s) stances.set(i, (s = inStance(set, i, cycle('idle', FRAMES, i * STANCE_PH))));
      return s;
    };
  }
  return set;
}

/**
 * Frames drawn in the figure's stance (`STANCE_PH`): standing and what is made from it — blinking
 * (an 'idle' frame), looking round, a gaze while standing, and the motions that land, stop or
 * settle into a stand. Everything else looks the same in every stance and is drawn once.
 */
export function standsIn(pose: Pose, ph: number): boolean {
  if (pose === 'idle' || pose === 'glance') return true;
  if (pose === 'gaze') return decodeGaze(ph % STANCE_PH).base === 'idle';
  if (pose !== 'motion') return false;
  const m = MOTIONS[Math.floor((ph % STANCE_PH) / FRAMES) % MOTIONS.length]!;
  return (m.keys as readonly Pose[]).includes('idle');
}

/**
 * `base` standing in its `k`th stance: its standing frames (`idle`) and every frame made from
 * standing asked for in that stance; everything else, views included, as `base` has it.
 */
function inStance(base: SpriteSet, k: number, idle: HTMLCanvasElement[]): SpriteSet {
  const s = Object.create(base) as SpriteSet;
  s.idle = idle;
  s.extra = (pose, ph = 0, blink, stand) =>
    base.extra!(
      pose,
      standsIn(pose, ph) ? ph + k * STANCE_PH : ph,
      blink,
      stand ?? (() => s.idle[pose === 'idle' ? ph % FRAMES : 0]!),
    );
  const views = base.views;
  if (views) {
    const own: Partial<Record<View8, SpriteSet>> = {};
    // The same views as the base set (every view but its own front3), so none lists as undefined.
    for (const v of OTHER_VIEWS.filter((v) => v in views))
      Object.defineProperty(own, v, {
        get: () => {
          const of = views[v];
          return of && (of.stance?.(k) ?? of);
        },
        enumerable: true,
      });
    s.views = own;
  }
  const focus = base.focus;
  if (focus)
    s.focus = (soon) => {
      focus(soon);
      // The player's own standing frames, in every view, as soon as the rest of their everyday ones.
      ahead(
        () => {
          for (const v of [s, ...OTHER_VIEWS.map((v) => s.views![v]!)])
            for (let i = 0; i < FRAMES; i++) void v.idle[i];
        },
        soon ? 'mineCore' : 'mineWarm',
      );
    };
  return s;
}

/**
 * Every figure in the game: people from their looks with the rig, each built the first time it is
 * shown, and the four-legged creatures as before.
 */
export function buildFigures(): Record<string, SpriteSet> {
  const out: Record<string, SpriteSet> = buildBeasts();
  for (const [k, look] of Object.entries(LOOKS)) {
    let set: SpriteSet | undefined;
    Object.defineProperty(out, k, { get: () => (set ??= rigSet(appearanceOf(look))), enumerable: true });
  }
  return out;
}
